import React, { useEffect, useState } from "react";
import PatientsSelector from "./overlay_components/PatientsSelector";

const WEEK_CODES = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const WEEKDAY_LABELS = {
  MO: "Montag",
  TU: "Dienstag",
  WE: "Mittwoch",
  TH: "Donnerstag",
  FR: "Freitag",
  SA: "Samstag",
  SU: "Sonntag",
};

export default function EventOverlay({
  open,
  mode = "create",
  initialDate,
  initialEvent,
  occurrenceStart = null,
  onClose,
  onSave,
  onDelete,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("11:00");

  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [weeksOfMonth, setWeeksOfMonth] = useState([]);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [hasEndDate, setHasEndDate] = useState(false);
  const [endDate, setEndDate] = useState("");

  const [patients, setPatients] = useState({ mode: "all", ids: [] });

  const [hasNotify, setHasNotify] = useState(true);
  const [notifyList, setNotifyList] = useState([30]);

  const [showDeleteOptions, setShowDeleteOptions] = useState(false);

  useEffect(() => {
    if (!open) return;

    setShowDeleteOptions(false);

    // IMPORTANT:
    // - in edit mode, prefill from initialEvent if provided
    // - otherwise fallback to create defaults
    if (mode === "edit" && initialEvent) {
      prefillFromEvent(initialEvent);
    } else if (initialEvent && mode !== "edit") {
      // for preview-edit flow we pass mode="create" but still want to edit that object
      prefillFromEvent(initialEvent);
    } else {
      prefillForCreate(initialDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialEvent, initialDate]);

  function prefillForCreate(seedDate) {
    const d = seedDate instanceof Date ? seedDate : new Date();
    const iso = toISODate(d);

    setTitle("");
    setDescription("");
    setDate(iso);
    setStartTime("09:00");
    setEndTime("11:00");

    setIsRecurring(false);
    setFrequency("daily");
    setWeeksOfMonth([]);
    setDaysOfWeek([]);
    setHasEndDate(false);
    setEndDate("");

    setPatients({ mode: "all", ids: [] });

    setHasNotify(true);
    setNotifyList([30]);
  }

  function prefillFromEvent(evt) {
    setTitle(evt.title || "");
    setDescription(evt.description || "");

    const s = new Date(evt.start);
    const e = new Date(evt.end);

    setDate(toISODate(s));
    setStartTime(toHHMM(s));
    setEndTime(toHHMM(e));

    const r = evt.recurrence || null;
    if (r) {
      setIsRecurring(true);
      setFrequency(r.frequency || "daily");

      if (r.frequency === "custom" && r.type === "monthly") {
        setWeeksOfMonth(Array.isArray(r.weeks_of_month) ? r.weeks_of_month.slice().sort((a, b) => a - b) : []);
        setDaysOfWeek(Array.isArray(r.days_of_week) ? r.days_of_week.slice() : []);
      } else {
        setWeeksOfMonth([]);
        setDaysOfWeek([]);
      }

      if (r.end_date) {
        const ed = new Date(r.end_date);
        setHasEndDate(true);
        setEndDate(toISODate(ed));
      } else {
        setHasEndDate(false);
        setEndDate("");
      }
    } else {
      setIsRecurring(false);
      setFrequency("daily");
      setWeeksOfMonth([]);
      setDaysOfWeek([]);
      setHasEndDate(false);
      setEndDate("");
    }

    setPatients(evt.patients || { mode: "all", ids: [] });

    const nm = evt.notification_minutes_before;
    const nmList = Array.isArray(nm) ? nm : typeof nm === "number" ? [nm] : [];

    if (nmList.length > 0) {
      setHasNotify(true);
      setNotifyList(nmList);
    } else {
      setHasNotify(false);
      setNotifyList([]);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    const [y, m, d] = date.split("-").map(Number);
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    const start = new Date(y, (m || 1) - 1, d || 1, sh || 0, sm || 0);
    const end = new Date(y, (m || 1) - 1, d || 1, eh || 0, em || 0);

    if (!(end > start)) {
      alert("Die Endzeit muss nach der Startzeit liegen.");
      return;
    }

    if (patients.mode === "custom" && patients.ids.length === 0) {
      alert("Bitte wähle mindestens einen Bewohner aus oder nutze 'Alle Bewohner'.");
      return;
    }

    let endDateObj = null;
    if (isRecurring && hasEndDate) {
      if (!endDate) {
        alert("Bitte wähle ein Enddatum aus oder deaktiviere das Enddatum.");
        return;
      }
      const [ey, emm, ed] = endDate.split("-").map(Number);
      endDateObj = new Date(ey, (emm || 1) - 1, ed || 1, 23, 59, 59);
      if (endDateObj < start) {
        alert("Das Enddatum muss am oder nach dem Startdatum des Termins liegen.");
        return;
      }
    }

    let notificationMinutesBefore = [];
    if (hasNotify) {
      if (!Array.isArray(notifyList) || notifyList.length === 0) {
        alert("Bitte füge mindestens eine Benachrichtigung hinzu oder deaktiviere Benachrichtigungen.");
        return;
      }
      const cleaned = [];
      for (const raw of notifyList) {
        const n = Number(raw);
        if (!Number.isInteger(n) || n <= 0) {
          alert("Benachrichtigungsminuten müssen positive ganze Zahlen sein.");
          return;
        }
        cleaned.push(Math.min(n, 10080));
      }
      notificationMinutesBefore = cleaned;
    } else {
      notificationMinutesBefore = [];
    }

    let recurrence = null;
    if (isRecurring) {
      if (frequency === "custom") {
        if (weeksOfMonth.length === 0 || daysOfWeek.length === 0) {
          alert("Bitte wähle mindestens eine Woche im Monat und mindestens einen Wochentag aus.");
          return;
        }
        recurrence = {
          frequency: "custom",
          type: "monthly",
          weeks_of_month: [...weeksOfMonth].sort((a, b) => a - b),
          days_of_week: [...daysOfWeek],
          end_date: endDateObj || null,
        };
      } else {
        recurrence = {
          frequency,
          end_date: endDateObj || null,
        };
      }
    }

    const base = {
      title: title.trim(),
      description,
      start,
      end,
      recurrence,
      patients,
      notification_minutes_before: notificationMinutesBefore,
      exclusions: Array.isArray(initialEvent?.exclusions) ? [...initialEvent.exclusions] : undefined,
    };

    const newEvent =
      mode === "edit" && initialEvent
        ? { ...initialEvent, ...base }
        : { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...base };

    onSave?.(newEvent);
    onClose?.();
  }

  function handleDeleteClick() {
    if (!initialEvent) return;
    const isSeries = !!initialEvent.recurrence;
    if (!isSeries) {
      onDelete?.(initialEvent.id);
      onClose?.();
      return;
    }
    setShowDeleteOptions(true);
  }

  function deleteOnlyThis() {
    if (!initialEvent || !occurrenceStart) return;
    const iso = occurrenceStart.toISOString();
    // Use originalStart/originalEnd (series start) so the PUT does not move
    // starts_at to the occurrence date, which would wipe past occurrences.
    const updated = {
      ...initialEvent,
      start: initialEvent.originalStart || initialEvent.start,
      end: initialEvent.originalEnd || initialEvent.end,
      exclusions: Array.isArray(initialEvent.exclusions)
        ? Array.from(new Set([...initialEvent.exclusions, iso]))
        : [iso],
    };
    onSave?.(updated);
    onClose?.();
  }

  function deleteThisAndFollowing() {
    if (!initialEvent || !occurrenceStart) return;
    const cutOff = new Date(occurrenceStart);
    cutOff.setDate(cutOff.getDate() - 1);
    cutOff.setHours(23, 59, 59, 999);
    // Use originalStart/originalEnd (series start) so the PUT does not move
    // starts_at to the occurrence date. Only end_date is changed.
    const updated = {
      ...initialEvent,
      start: initialEvent.originalStart || initialEvent.start,
      end: initialEvent.originalEnd || initialEvent.end,
      recurrence: initialEvent.recurrence ? { ...initialEvent.recurrence, end_date: cutOff } : null,
    };
    onSave?.(updated);
    onClose?.();
  }

  function deleteAll() {
    if (!initialEvent) return;
    onDelete?.(initialEvent.id);
    onClose?.();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />

      <form onSubmit={handleSubmit} className="relative z-10 w-full max-w-5xl rounded-2xl bg-white shadow-lg">
        <div className="flex items-center justify-between px-6 py-4 rounded-t-2xl bg-blue-50 border-b">
          <h2 className="text-xl font-semibold">
            {mode === "edit" ? "Termin bearbeiten" : "Termin erstellen"}
          </h2>

          {mode === "edit" && initialEvent && (
            <div className="flex items-center gap-2">
              {!showDeleteOptions ? (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="text-red-600 text-sm border border-red-200 px-3 py-1 rounded hover:bg-red-50"
                >
                  Löschen
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded px-2 py-1 text-sm">
                  <div className="mb-1 text-red-700 font-medium">Wiederkehrende Termine löschen?</div>
                  {initialEvent.recurrence && occurrenceStart ? (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={deleteOnlyThis} className="px-2 py-1 rounded border hover:bg-white">
                        Nur diesen
                      </button>
                      <button
                        type="button"
                        onClick={deleteThisAndFollowing}
                        className="px-2 py-1 rounded border hover:bg-white"
                      >
                        Diesen & folgende
                      </button>
                      <button
                        type="button"
                        onClick={deleteAll}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-white"
                      >
                        Alle löschen
                      </button>
                      <button type="button" onClick={() => setShowDeleteOptions(false)} className="px-2 py-1 rounded">
                        Abbrechen
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={deleteAll}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-white"
                      >
                        Löschen
                      </button>
                      <button type="button" onClick={() => setShowDeleteOptions(false)} className="px-2 py-1 rounded">
                        Abbrechen
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <div className="border rounded-lg p-3 bg-white">
              <PatientsSelector
                value={patients}
                onChange={setPatients}
                seriesId={mode === "edit" ? (initialEvent?.series_id ?? initialEvent?.id) : undefined}
                autoHydrateFromSeries={mode === "edit"}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Titel</label>
              <input className="mt-1 w-full border rounded px-3 py-2 bg-white" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
              <textarea className="mt-1 w-full border rounded px-3 py-2 bg-white min-h-[96px]" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700">Datum</label>
                <input type="date" className="mt-1 w-full border rounded px-3 py-2 bg-white" value={date} onChange={(e) => setDate(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Start</label>
                <input type="time" className="mt-1 w-full border rounded px-3 py-2 bg-white" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Ende</label>
                <input type="time" className="mt-1 w-full border rounded px-3 py-2 bg-white" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
              </div>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <div className="border rounded-lg p-3 bg-white space-y-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hasNotify}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHasNotify(checked);
                    if (checked && notifyList.length === 0) setNotifyList([30]);
                    if (!checked) setNotifyList([]);
                  }}
                />
                <span>Benachrichtigung(en) vor Termin</span>
              </label>

              {hasNotify && (
                <div className="space-y-2">
                  {notifyList.map((val, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        className="w-32 border rounded px-3 py-2 bg-white"
                        value={val}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = Number(v);
                          if (v === "" || Number.isFinite(n)) {
                            setNotifyList((prev) => {
                              const next = [...prev];
                              next[idx] = v === "" ? "" : n;
                              return next;
                            });
                          }
                        }}
                      />
                      <span className="text-sm text-gray-600">Minuten vorher</span>
                      {notifyList.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setNotifyList((prev) => prev.filter((_, i) => i !== idx))}
                          className="text-xs text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-50"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => setNotifyList((prev) => [...prev, 30])}
                    className="mt-1 text-sm text-blue-600 hover:underline"
                  >
                    + Weitere Benachrichtigung hinzufügen
                  </button>
                </div>
              )}
            </div>

            <div className="border rounded-lg p-3 bg-white space-y-3">
              <label className="inline-flex items-center">
                <input type="checkbox" className="mr-2" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} />
                Wiederkehrender Termin
              </label>

              {isRecurring && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium">Enddatum (optional)</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="checkbox" checked={hasEndDate} onChange={(e) => setHasEndDate(e.target.checked)} />
                      <input
                        type="date"
                        className="w-full border rounded px-3 py-2 disabled:opacity-50 bg-white"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!hasEndDate}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Typ</label>
                    <select className="w-full border rounded px-3 py-2 mt-1 bg-white" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                      <option value="daily">Täglich</option>
                      <option value="weekly">Wöchentlich</option>
                      <option value="biweekly">Alle 2 Wochen</option>
                      <option value="triweekly">Alle 3 Wochen</option>
                      <option value="custom">Benutzerdefiniert (monatlich)</option>
                    </select>
                  </div>

                  {frequency === "custom" && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">
                        Monatlich (nach n-tem Wochentag): Wochennummer(n) & Wochentag(e) auswählen
                      </p>

                      <div>
                        <div className="text-sm font-medium mb-1">Kalenderwoche im Monat</div>
                        <div className="flex flex-wrap gap-3">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <label key={n} className="inline-flex items-center">
                              <input
                                type="checkbox"
                                className="mr-1"
                                checked={weeksOfMonth.includes(n)}
                                onChange={() =>
                                  setWeeksOfMonth((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]))
                                }
                              />
                              {n}{getOrdinalSuffix(n)}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-medium mb-1">Wochentage</div>
                        <div className="flex flex-wrap gap-3">
                          {WEEK_CODES.map((code) => (
                            <label key={code} className="inline-flex items-center">
                              <input
                                type="checkbox"
                                className="mr-1"
                                checked={daysOfWeek.includes(code)}
                                onChange={() =>
                                  setDaysOfWeek((prev) => (prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code]))
                                }
                              />
                              {WEEKDAY_LABELS[code]}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t rounded-b-2xl bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">
            Abbrechen
          </button>
          <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
}

function toHHMM(date) {
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}
function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function getOrdinalSuffix() {
  return ".";
}
